/**
 * ResultCard.jsx
 * Wider horizontal layout for recommended card, compact for alternatives.
 * Bank logo left, rate + payment center, reasons + CTA right.
 */
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
  Collapse,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { bankLogos, bankColors, bankURLs, getInitials, bankMeta } from '../mortgageConstants';
import { formatCurrency } from '../mortgageCalcEngine';
import { trackWizardCtaClick } from './analytics';

const BankLogo = ({ bankName, size = 'default' }) => {
  const [imgError, setImgError] = useState(false);
  const src = bankLogos[bankName];
  const color = bankColors[bankName] || '#1E3A5F';
  const w = size === 'large' ? 64 : 48;
  const h = size === 'large' ? 44 : 32;

  if (!src || imgError) {
    return (
      <Box
        sx={{
          width: w, height: h, borderRadius: '10px', bgcolor: color,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: size === 'large' ? 14 : 11 }}>
          {getInitials(bankName)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: w, height: h, borderRadius: '10px', bgcolor: '#F7F9FC',
        border: 'none', display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexShrink: 0, overflow: 'hidden', p: 0.5,
      }}
    >
      <Box
        component="img" src={src} alt={bankName} onError={() => setImgError(true)}
        width={w - 8} height={h - 8}
        sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
      />
    </Box>
  );
};

/* ── Recommended card (full-width horizontal) ── */
const RecommendedCard = ({ result }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const url = bankURLs[result.bankName];
  const meta = result.meta || bankMeta[result.bankName] || {};

  return (
    <Card
      sx={{
        borderRadius: '16px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        border: 'none',
        borderTop: '2px solid #F9A825',
        bgcolor: '#FFFDF7',
        transition: 'box-shadow 0.22s ease',
        '&:hover': { boxShadow: '0 6px 24px rgba(0,0,0,0.08)' },
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        {/* Label */}
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            fontSize: '12px', fontWeight: 600, color: '#C68A00',
            textTransform: 'uppercase', letterSpacing: '0.08em', mb: 2,
            bgcolor: '#FFF8E1', px: 1.5, py: 0.5, borderRadius: '6px',
          }}
        >
          {t('mortgageWizard.results.recommended')}
        </Box>

        {/* Main row: logo + info | rate + payment | CTA */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'center' },
            gap: { xs: 2, md: 3 },
          }}
        >
          {/* Left: Logo + bank info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: '1 1 auto', minWidth: 0 }}>
            <BankLogo bankName={result.bankName} size="large" />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1A1A1A', lineHeight: 1.3 }}>
                {result.bankName}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>
                  {result.term}
                </Typography>
                {meta.lenderType && (
                  <Chip
                    label={meta.lenderType}
                    size="small"
                    sx={{ height: 18, fontSize: '12px', fontWeight: 600, bgcolor: '#F7F9FC', color: '#5F6B7A' }}
                  />
                )}
                {meta.newcomer && (
                  <Chip
                    label={t('mortgageWizard.results.newcomerTag')}
                    size="small"
                    sx={{
                      height: 18, fontSize: '12px', fontWeight: 600,
                      bgcolor: '#E8F5E9', color: '#2E7D32',
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Center: Rate + Payment */}
          <Box sx={{ textAlign: { xs: 'left', md: 'center' }, flexShrink: 0, py: { md: 1 }, px: { md: 2 } }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3rem' }, fontWeight: 800,
                  color: '#0F1E3A', lineHeight: 1,
                  fontFeatureSettings: "'tnum'", letterSpacing: '-0.03em',
                }}
              >
                {result.rate}
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 700,
                  color: '#0F1E3A', lineHeight: 1, ml: 0.25,
                }}
              >
                %
              </Typography>
            </Box>
            {result.payment > 0 && (
              <Typography sx={{ fontSize: '14px', color: '#5F6B7A', mt: 0.5, fontFeatureSettings: "'tnum'" }}>
                {formatCurrency(result.payment)} {t('mortgageWizard.results.perMonth')}
              </Typography>
            )}
          </Box>

          {/* Right: CTA */}
          <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: { md: 'flex-end' } }}>
            <Button
              variant="contained"
              href={url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon sx={{ fontSize: '16px' }} />}
              onClick={() => trackWizardCtaClick('see_offer', result.bankName)}
              sx={{
                bgcolor: '#1E3A5F', borderRadius: '10px', py: 1.25, px: 3.5,
                fontWeight: 700, textTransform: 'none', fontSize: '15px',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(30,58,95,0.20)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#1A3152',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 14px rgba(30,58,95,0.30)',
                },
                '&:hover .MuiButton-endIcon': { transform: 'translate(2px, -2px)' },
                '& .MuiButton-endIcon': { transition: 'transform 0.2s ease' },
                '@keyframes fadeInCta': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                animation: 'fadeInCta 0.3s ease 0.1s both',
              }}
            >
              {t('mortgageWizard.results.seeOffer')}
            </Button>
          </Box>
        </Box>

        {/* Expandable reasons */}
        {result.reasons && result.reasons.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box
              onClick={() => setExpanded(!expanded)}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none' }}
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); }
              }}
            >
              <Typography sx={{ fontSize: '12px', color: '#5F6B7A', fontWeight: 500 }}>
                {t('mortgageWizard.results.whyThisOption')}
              </Typography>
              {expanded
                ? <KeyboardArrowUpIcon sx={{ fontSize: '16px', color: '#5F6B7A' }} />
                : <KeyboardArrowDownIcon sx={{ fontSize: '16px', color: '#5F6B7A' }} />
              }
            </Box>
            <Collapse in={expanded}>
              <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                {result.reasons.map((reasonKey, i) => (
                  <Typography
                    component="li"
                    key={i}
                    sx={{ fontSize: '12px', color: '#5F6B7A', lineHeight: 1.6 }}
                  >
                    {t(`mortgageWizard.reasons.${reasonKey}`, { defaultValue: reasonKey })}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

/* ── Alternative card (compact vertical) ── */
const AlternativeCard = ({ result }) => {
  const { t } = useTranslation();
  const url = bankURLs[result.bankName];
  const meta = result.meta || bankMeta[result.bankName] || {};

  return (
    <Card
      sx={{
        borderRadius: '14px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease',
        border: 'none',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
          transform: 'translateY(-3px)',
        },
      }}
    >
      <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* Bank row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <BankLogo bankName={result.bankName} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>
              {result.bankName}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>
              {result.term}
            </Typography>
          </Box>
        </Box>

        {/* Rate */}
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'baseline', justifyContent: 'center' }}>
            <Typography
              component="span"
              sx={{
                fontSize: { xs: '2rem', md: '2.25rem' }, fontWeight: 700,
                color: '#0F1E3A', lineHeight: 1,
                fontFeatureSettings: "'tnum'", letterSpacing: '-0.02em',
              }}
            >
              {result.rate}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' }, fontWeight: 600,
                color: '#0F1E3A', lineHeight: 1, ml: 0.25,
              }}
            >
              %
            </Typography>
          </Box>
          {result.payment > 0 && (
            <Typography sx={{ fontSize: '13px', color: '#5F6B7A', mt: 0.75, fontFeatureSettings: "'tnum'" }}>
              {formatCurrency(result.payment)} {t('mortgageWizard.results.perMonth')}
            </Typography>
          )}
        </Box>

        {/* Tags */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, justifyContent: 'center' }}>
          {meta.newcomer && (
            <Chip
              label={t('mortgageWizard.results.newcomerTag')}
              size="small"
              sx={{
                height: 20, fontSize: '12px', fontWeight: 600,
                bgcolor: '#E8F5E9', color: '#2E7D32',
              }}
            />
          )}
          {meta.lenderType && (
            <Chip
              label={meta.lenderType}
              size="small"
              sx={{ height: 20, fontSize: '12px', fontWeight: 600, bgcolor: '#F7F9FC', color: '#5F6B7A' }}
            />
          )}
        </Box>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* CTA */}
        <Button
          variant="contained"
          fullWidth
          href={url || '#'}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<OpenInNewIcon sx={{ fontSize: '16px' }} />}
          onClick={() => trackWizardCtaClick('see_offer', result.bankName)}
          sx={{
            mt: 2,
            bgcolor: '#1E3A5F', borderRadius: '10px', py: 1,
            fontWeight: 600, textTransform: 'none', fontSize: '13px',
            transition: 'all 0.2s ease',
            '&:hover': { bgcolor: '#1A3152', transform: 'translateY(-1px)', boxShadow: '0 3px 10px rgba(30,58,95,0.25)' },
            '&:hover .MuiButton-endIcon': { transform: 'translate(2px, -2px)' },
            '& .MuiButton-endIcon': { transition: 'transform 0.2s ease' },
          }}
        >
          {t('mortgageWizard.results.seeOffer')}
        </Button>
      </CardContent>
    </Card>
  );
};

const ResultCard = ({ result, isPrimary }) => {
  if (isPrimary) return <RecommendedCard result={result} />;
  return <AlternativeCard result={result} />;
};

export default ResultCard;
